import { useEffect, useState } from 'react';

import { apiUrl } from '@ai-studio/shared';

import { ApiError, api } from '../lib/api.js';
import { hasDesktopBridge, pickFolder } from '../lib/desktop.js';
import { WEBSITE } from '../lib/edition.js';
import { useWorkspace } from '../state/workspace.js';
import { LibraryPublish } from './build-window/LibraryPublish.js';
import { PublishPanel } from './website/PublishPanel.js';

/**
 * The Export button's dialog: every way a game leaves the editor, in one place.
 *
 * - Windows game (DirectX 12 or Vulkan), web build: the desktop app writes it
 *   into a folder (`prism export` through the Build window's job); the website
 *   builds it on its server and downloads it as a .zip.
 * - Dedicated server: a headless server to run yourself (the website builds a
 *   Linux one; the desktop app builds one for this PC).
 * - Prism Library: publish to the public game library on the website.
 */

type Target = 'windows-dx12' | 'windows-vulkan' | 'web' | 'server' | 'library';

const TARGETS: { id: Target; label: string; note: string; desktopOnly?: boolean }[] = [
  { id: 'windows-dx12', label: 'Windows (DirectX 12)', note: 'A game folder with its .exe, for any Windows PC.' },
  { id: 'windows-vulkan', label: 'Windows (Vulkan)', note: 'The same game, drawing with Vulkan.' },
  { id: 'web', label: 'Web', note: 'Plays in a browser: put it on any web host. Online play by join code or your own server.' },
  { id: 'server', label: 'Dedicated server', note: WEBSITE ? 'A headless Linux server for the game, to run on your own machine or host.' : 'A headless server for the game, to run yourself.' },
  { id: 'library', label: 'Prism Engine Cloud Library', note: 'Publish to the Prism Engine Cloud website: playable by anyone, at once.' },
];

interface Job {
  status: 'running' | 'succeeded' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  error: string | null;
  outputDir: string;
}

export function ExportDialog({ onClose }: { onClose: () => void }) {
  const projectId = useWorkspace((s) => s.projectId);
  const projectName = useWorkspace((s) => s.projectName);
  const [target, setTarget] = useState<Target>('windows-dx12');
  const [folder, setFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [download, setDownload] = useState<{ url: string; name: string; bytes: number } | null>(null);

  const targets = TARGETS.filter((t) => !(WEBSITE && t.desktopOnly));

  // Desktop: the default folder next to the project, and the running job's progress.
  useEffect(() => {
    if (WEBSITE || !projectId) return;
    void api
      .get<{ defaultOutputDir: string }>(`/projects/${projectId}/build?profile=${encodeURIComponent(`Export ${target}`)}`)
      .then((r) => setFolder((f) => f || r.defaultOutputDir))
      .catch(() => undefined);
  }, [projectId, target]);

  useEffect(() => {
    if (WEBSITE || !projectId || !busy) return;
    const timer = window.setInterval(async () => {
      const r = await api.get<{ job: Job | null }>(`/projects/${projectId}/build`).catch(() => null);
      if (!r?.job) return;
      setJob(r.job);
      if (r.job.status !== 'running') {
        setBusy(false);
        if (r.job.status === 'failed') setError(r.job.error ?? 'The export failed.');
      }
    }, 700);
    return () => window.clearInterval(timer);
  }, [projectId, busy]);

  async function run() {
    if (!projectId || target === 'library') return;
    setBusy(true);
    setError(null);
    setJob(null);
    setDownload(null);
    try {
      if (WEBSITE) {
        const r = await api.post<{ download: string; name: string; bytes: number }>(`/projects/${projectId}/export`, { target });
        setDownload({ url: apiUrl(r.download), name: r.name, bytes: r.bytes });
        setBusy(false);
        return;
      }
      await api.post(`/projects/${projectId}/build`, {
        profile: `Export ${target}`,
        target: target === 'server' ? 'server' : target === 'web' ? 'web' : 'game',
        ...(target === 'windows-dx12' ? { graphics: 'dx12' } : target === 'windows-vulkan' ? { graphics: 'vulkan' } : {}),
        outputDir: folder.trim() || undefined,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The export failed.');
      setBusy(false);
    }
  }

  const chosen = targets.find((t) => t.id === target)!;
  return (
    <div className="export-backdrop" role="dialog" aria-modal="true" aria-label="Export" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="export-dialog">
        <header className="export-head">
          <strong>Export {projectName}</strong>
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            Close
          </button>
        </header>
        <div className="export-body">
          <ul className="export-targets" role="listbox" aria-label="Export to">
            {targets.map((t) => (
              <li key={t.id}>
                <button type="button" className={`export-target${t.id === target ? ' is-active' : ''}`} onClick={() => !busy && setTarget(t.id)}>
                  <strong>{t.label}</strong>
                  <span className="muted">{t.note}</span>
                </button>
              </li>
            ))}
          </ul>
          <section className="export-main">
            {target === 'library' ? (
              WEBSITE ? (
                <PublishPanel />
              ) : projectId !== null ? (
                <LibraryPublish projectId={projectId} projectName={projectName || 'My Game'} />
              ) : null
            ) : (
              <>
                <h3>{chosen.label}</h3>
                <p className="muted">{chosen.note}</p>
                {!WEBSITE && (
                  <label className="field">
                    <span>Folder (outside the project)</span>
                    <div className="field-row">
                      <input className="mono" value={folder} onChange={(e) => setFolder(e.target.value)} disabled={busy} />
                      {hasDesktopBridge() && (
                        <button type="button" className="ghost" disabled={busy} onClick={async () => setFolder((await pickFolder()) ?? folder)}>
                          Browse…
                        </button>
                      )}
                    </div>
                  </label>
                )}
                {WEBSITE && <p className="muted">The website builds it and gives you a .zip to download (kept for an hour).</p>}
                <button type="button" className="primary" disabled={busy || !projectId} onClick={() => void run()}>
                  {busy ? 'Exporting…' : 'Export'}
                </button>
                {job && (
                  <div className="export-progress">
                    <div className="export-bar">
                      <div style={{ width: `${Math.round((job.status === 'succeeded' ? 1 : job.progress) * 100)}%` }} />
                    </div>
                    <span className="muted">{job.status === 'succeeded' ? `Done: ${job.outputDir}` : job.status === 'running' ? job.message : job.status}</span>
                  </div>
                )}
                {download && (
                  <p>
                    Ready:{' '}
                    <a href={download.url} download={download.name}>
                      {download.name}
                    </a>{' '}
                    <span className="muted">({Math.round(download.bytes / 1048576)} MB)</span>
                  </p>
                )}
                {error && (
                  <p className="login-error" role="alert">
                    {error}
                  </p>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
